import type { IOperationError } from '@hc-frontend/core-utils-business-operation';

import type { Log, LogContext, LogType } from '../../entities';
import type {
  PerformFallbackLogRepository,
  PerformSafeLogRepository,
} from '../../repositories';

const hierarchy = {
  debug: ['debug', 'info', 'warn', 'error'],
  info: ['info', 'warn', 'error'],
  warn: ['warn', 'error'],
  error: ['error'],
};

function createErrorLogs<TError extends string>(
  context: LogContext,
  errors: IOperationError<TError>[],
): Log[] {
  return errors.map((error) => ({
    context,
    type: 'error',
    message: [
      `Error code: ${error.code}`,
      error.rootCause?.name,
      error.rootCause?.message,
    ]
      .filter(Boolean)
      .join(' - '),
    data: error.info,
  }));
}

export async function logOperationItemUC(
  provider: PerformFallbackLogRepository | PerformSafeLogRepository,
  context: LogContext,
  level: LogType,
  log: Log,
): Promise<void> {
  // Only log if the log type is in the hierarchy
  if (!hierarchy[level].includes(log.type)) {
    return Promise.resolve();
  }

  // If the provider is just a safe log, then just log
  if (typeof provider === 'function') {
    return provider(log);
  }

  // If the provider is a fallback repository, then try to log with the main option and use the fallback if it is a failed operation
  const operationResult = await provider.main(log);
  if (operationResult?.success === false)
    await Promise.all(
      createErrorLogs(context, operationResult.errors).map(provider.fallback),
    );
}
